/**
 * Working document slice (RTK).
 * List state from fetchWorkingDocuments thunk lifecycle (pending/fulfilled/rejected).
 * No dispatches in thunk – simple API call like objectsSlice.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getWorkingDocuments } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import { trackRequest } from "../../utils/requestTracker";
import {
  stopOneDriveDocumentSharing,
  removeOneDriveSharingLink,
  markOneDriveDocumentShared,
} from "../oneDrive/oneDriveSharingDetailsSlice";

const userAuth = new UserAuth();

function applySharingDetailsToWorkingList(state, action) {
  const documentId = action.payload?.documentId;
  if (!documentId) return;
  const isShared = action.payload?.isShared === true;
  const existing = Array.isArray(state.workingDocumentList)
    ? state.workingDocumentList
    : [];
  state.workingDocumentList = existing.map((doc) => {
    if (doc?.id !== documentId && doc?.Id !== documentId) return doc;
    return {
      ...doc,
      shared: isShared ? doc.shared : null,
      Shared: isShared ? doc.Shared : null,
      isShared,
    };
  });
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return "";
}

function normalizeIdentifier(value) {
  return firstNonEmpty(value).toUpperCase();
}

function getMetaDataObject(file) {
  const rawMetaData = file?.MetaData ?? file?.metaData ?? file?.metadata;
  if (!rawMetaData) return null;
  if (typeof rawMetaData === "object") return rawMetaData;
  if (typeof rawMetaData !== "string") return null;
  try {
    return JSON.parse(rawMetaData);
  } catch {
    return null;
  }
}

function parseIfsIdentifiersFromFileName(fileName) {
  if (!fileName) return {};
  const openParen = fileName.lastIndexOf("(");
  const closeParen = fileName.indexOf(")", openParen >= 0 ? openParen : 0);
  if (openParen < 0 || closeParen <= openParen) return {};
  const inside = fileName.slice(openParen + 1, closeParen).trim();
  if (!inside) return {};
  const parts = inside.split(" - ");
  if (parts.length < 4) return {};
  return {
    docClass: parts[0]?.trim(),
    docNo: parts[1]?.trim(),
    docSheet: parts[2]?.trim(),
    docRev: parts[3]?.trim(),
  };
}

function getWorkingDocumentIdentifiers(file) {
  const metaData = getMetaDataObject(file) ?? {};
  const fromName = parseIfsIdentifiersFromFileName(file?.name ?? "");

  return {
    docClass: normalizeIdentifier(
      file?.docClass,
      file?.DocClass,
      file?.class,
      file?.Class,
      metaData?.docClass,
      metaData?.DocClass,
      metaData?.class,
      metaData?.Class,
      fromName?.docClass,
    ),
    docNo: normalizeIdentifier(
      file?.docNo,
      file?.DocNo,
      metaData?.docNo,
      metaData?.DocNo,
      fromName?.docNo,
    ),
    docSheet: normalizeIdentifier(
      file?.docSheet,
      file?.DocSheet,
      file?.sheetNo,
      file?.SheetNo,
      metaData?.docSheet,
      metaData?.DocSheet,
      metaData?.sheetNo,
      metaData?.SheetNo,
      fromName?.docSheet,
    ),
    docRev: normalizeIdentifier(
      file?.docRev,
      file?.DocRev,
      metaData?.docRev,
      metaData?.DocRev,
      fromName?.docRev,
    ),
    fileNo: normalizeIdentifier(
      file?.fileNo,
      file?.FileNo,
      metaData?.fileNo,
      metaData?.FileNo,
    ),
  };
}

function isMatchingWorkingDocument(file, target) {
  const fileIds = getWorkingDocumentIdentifiers(file);
  const targetIds = {
    docClass: normalizeIdentifier(target?.class, target?.docClass),
    docNo: normalizeIdentifier(target?.docNo),
    docSheet: normalizeIdentifier(target?.sheetNo, target?.docSheet),
    docRev: normalizeIdentifier(target?.docRev),
    fileNo: normalizeIdentifier(target?.fileNo),
  };

  if (!fileIds.docClass || !fileIds.docNo || !fileIds.docRev) {
    return false;
  }

  if (
    fileIds.docClass !== targetIds.docClass ||
    fileIds.docNo !== targetIds.docNo ||
    fileIds.docRev !== targetIds.docRev
  ) {
    return false;
  }

  if (
    targetIds.docSheet &&
    fileIds.docSheet &&
    fileIds.docSheet !== targetIds.docSheet
  ) {
    return false;
  }

  if (
    targetIds.fileNo &&
    fileIds.fileNo &&
    fileIds.fileNo !== targetIds.fileNo
  ) {
    return false;
  }

  return true;
}

export const fetchWorkingDocuments = createAsyncThunk(
  "workingDocument/fetchWorkingDocuments",
  async (
    {
      teamId,
      channelId,
      tenantId,
      fileRoot,
      displayCount = 20,
      recFrom = 0,
      recSize = 20,
      append = false,
      searchText = "",
      fileType = "''",
    },
    { rejectWithValue },
  ) => {
    logger.info("fetchWorkingDocuments start", null, "REDUX");
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        logger.error(
          "fetchWorkingDocuments no access token",
          tokenResponse,
          "REDUX",
        );
        return rejectWithValue({
          message: "No access token",
          code: "UNAUTHORIZED",
        });
      }
      const reqKey = [
        "workingDocuments",
        teamId ?? "",
        channelId ?? "",
        tenantId ?? "",
        fileRoot ?? "",
        `displayCount:${displayCount}`,
        `recFrom:${recFrom}`,
        `recSize:${recSize}`,
        `searchText:${searchText ?? ""}`,
        `fileType:${fileType ?? "''"}`,
      ].join("|");

      const { data, status } = await trackRequest(reqKey, () =>
        getWorkingDocuments(accessToken, {
          teamId,
          channelId,
          tenantId,
          fileRoot,
          displayCount,
          recFrom,
          recSize,
          searchText,
          fileType,
        })
      );
      if (status === 200) {
        logger.info("fetchWorkingDocuments success", { status }, "REDUX");
        return {
          items: data ?? [],
          append,
        };
      }
      if (status === 204) {
        return {
          items: [],
          append,
        };
      }
      logger.error("fetchWorkingDocuments non-200/204", { status }, "REDUX");
      return rejectWithValue({ message: `Status ${status}`, code: status });
    } catch (err) {
      logger.error("fetchWorkingDocuments failed", err, "REDUX");
      return rejectWithValue({
        message: err?.message ?? "Request failed",
        code: err?.response?.status,
      });
    }
  },
);

const initialState = {
  workingDocumentList: [],
  listLoading: false,
  listError: null,
  listQuery: { searchText: "", fileType: "''" },
  /** Server may return further pages when the last fetch was a full chunk (recFrom/recSize paging). */
  hasMoreServerDocuments: true,
  /** Incremented when Working Documents list should refetch (sidebar/accordion re-click). */
  refreshTrigger: 0,
};

const workingDocumentSlice = createSlice({
  name: "workingDocument",
  initialState,
  reducers: {
    requestRefreshWorkingDocuments: (state) => {
      state.refreshTrigger = (state.refreshTrigger || 0) + 1;
    },
    removeWorkingDocumentByIdentifiers: (state, action) => {
      const target = action.payload;
      if (!target) return;

      const existing = Array.isArray(state.workingDocumentList)
        ? state.workingDocumentList
        : [];

      state.workingDocumentList = existing.filter(
        (file) => !isMatchingWorkingDocument(file, target),
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkingDocuments.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchWorkingDocuments.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listError = null;
        const arg = action.meta.arg;
        state.listQuery = {
          searchText: arg.searchText ?? "",
          fileType: arg.fileType ?? "''",
        };
        const items = Array.isArray(action.payload?.items)
          ? action.payload.items
          : [];
        const append = action.payload?.append === true;
        const recSize = arg.recSize ?? arg.displayCount ?? 20;
        if (!append) {
          state.workingDocumentList = items;
          state.hasMoreServerDocuments =
            items.length > 0 && items.length >= recSize;
          return;
        }
        const existing = Array.isArray(state.workingDocumentList)
          ? state.workingDocumentList
          : [];
        const merged = [...existing];
        const getDocKey = (doc) =>
          `${doc?.id ?? ""}|${doc?.name ?? ""}|${doc?.DocNo ?? ""}|${doc?.DocRev ?? ""}`;
        const seen = new Set(existing.map((doc) => getDocKey(doc)));
        items.forEach((doc) => {
          const key = getDocKey(doc);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(doc);
          }
        });
        state.workingDocumentList = merged;
        if (items.length === 0) {
          state.hasMoreServerDocuments = false;
        } else {
          state.hasMoreServerDocuments = items.length >= recSize;
        }
      })
      .addCase(fetchWorkingDocuments.rejected, (state, action) => {
        state.listLoading = false;
        state.listError =
          action.payload?.message ?? action.error?.message ?? true;
        state.workingDocumentList = [];
        state.hasMoreServerDocuments = false;
      })
      // Both resolve with refreshed sharing details; sync the row's Sharing status.
      .addCase(stopOneDriveDocumentSharing.fulfilled, applySharingDetailsToWorkingList)
      .addCase(removeOneDriveSharingLink.fulfilled, applySharingDetailsToWorkingList)
      .addCase(markOneDriveDocumentShared, (state, action) => {
        const documentId = action.payload?.documentId;
        if (!documentId) return;
        const existing = Array.isArray(state.workingDocumentList)
          ? state.workingDocumentList
          : [];
        state.workingDocumentList = existing.map((doc) =>
          doc?.id === documentId || doc?.Id === documentId
            ? { ...doc, isShared: true }
            : doc,
        );
      });
  },
});

export const {
  removeWorkingDocumentByIdentifiers,
  requestRefreshWorkingDocuments,
} = workingDocumentSlice.actions;
export default workingDocumentSlice;
